import { useEffect, useRef, useState } from 'react';
import { LIMITS } from '@bunker/shared';
import { useTranslation } from 'react-i18next';
import { Popover, SearchField } from '@heroui/react';
import { api } from '@/lib/api';
import { humanizeApiError } from '@/lib/humanizeApiError';
import { stripMarkdown } from '@/lib/markdown';
import { formatTime } from '@/lib/format';
import { SearchIcon } from '@/components/icons';
import { Tooltip } from '@/components/Tooltip';
import { emitAppEvent } from '@/lib/appEvents';
import type { Message } from '@/types/api';

/**
 * 🔍 icon + popover. Search is per-channel by design: cross-channel
 * search needs ACL filtering on every guild membership, which is its
 * own feature. For now this hits the simple ILIKE endpoint and shows
 * up to 50 newest hits.
 *
 * Debounced 250ms so typing doesn't fire a request per keystroke.
 */
export function SearchButton(props: { channelId: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset state when the popover toggles closed or the channel changes.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults(null);
    setError(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open, props.channelId]);

  // Debounced fetch: wait 250ms after the last keystroke before hitting
  // the server. Empty query → clear results without a round-trip.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    // Shorter queries are not searched (the server answers them empty).
    if (q.length < LIMITS.search.minQueryLength) {
      setResults(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const handle = window.setTimeout(() => {
      api
        .searchMessages(props.channelId, q)
        .then((res) => {
          if (cancelled) return;
          setResults(res.messages);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(humanizeApiError(err, t('ui.search.error')).title);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query, open, props.channelId, t]);

  return (
    <Popover isOpen={open} onOpenChange={setOpen}>
      <Tooltip label={t('ui.search.searchInChannel')}>
        <Popover.Trigger
          aria-label={t('ui.search.searchInChannel')}
          className="group rounded-lg p-2 text-ink-dim transition-colors hover:bg-white/10 hover:text-accent"
        >
          <SearchIcon className="h-[22px] w-[22px] group-hover:[animation:icon-shake_0.4s_ease-in-out_2]" />
        </Popover.Trigger>
      </Tooltip>
      <Popover.Content className="w-96 max-w-[calc(100vw-1.5rem)]" placement="bottom end">
        <Popover.Dialog className="overflow-hidden p-0">
        <div className="p-2">
          <SearchField aria-label={t('ui.search.searchInChannel')} variant="secondary" value={query} onChange={setQuery}>
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input
                ref={inputRef}
                placeholder={t('ui.search.placeholder')}
                autoComplete="off"
                spellCheck={false}
                maxLength={120}
              />
              <SearchField.ClearButton aria-label={t('ui.search.clear')} />
            </SearchField.Group>
          </SearchField>
        </div>
        <div className="scrollbar-thin max-h-[60vh] overflow-y-auto">
          {loading && (
            <div className="px-4 py-6 text-center text-xs text-ink-muted">
              {t('ui.search.searching')}
            </div>
          )}
          {error && (
            <div className="px-4 py-3 text-xs text-danger-ink">{error}</div>
          )}
          {!loading && !error && results && results.length === 0 && (
            <div className="px-4 py-6 text-center text-xs text-ink-muted">
              {t('ui.search.noResults', { query: query.trim() })}
            </div>
          )}
          {!loading &&
            !error &&
            results &&
            results.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => {
                  emitAppEvent('chat:scroll-to-message', { messageId: m.id });
                  setOpen(false);
                }}
                className="block w-full px-4 py-3 text-left transition-colors hover:bg-accent/10"
                style={{ borderBottom: '1px solid rgb(var(--ring-inset) / var(--ring-inset-alpha))' }}
              >
                <header className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="truncate text-xs font-light tracking-[-0.015em] text-accent">
                    {m.author.displayName || m.author.username}
                  </span>
                  <span className="shrink-0 text-[10px] text-ink-muted">
                    {formatTime(new Date(m.createdAt))}
                  </span>
                </header>
                <p className="line-clamp-3 text-xs leading-snug text-ink">
                  {highlightHit(stripMarkdown(m.content), query.trim())}
                </p>
              </button>
            ))}
        </div>
        </Popover.Dialog>
      </Popover.Content>
    </Popover>
  );
}

/**
 * Highlight every case-insensitive occurrence of `query` inside `text`.
 * Returns React fragments — never raw HTML, never dangerouslySetInnerHTML.
 * Limits to 200 chars around the first hit so a long message doesn't
 * dominate the popover.
 */
function highlightHit(text: string, query: string) {
  if (!query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const firstHit = lower.indexOf(q);
  if (firstHit < 0) return text;
  // Centre a 240-char window on the first hit so long messages stay readable.
  const start = Math.max(0, firstHit - 60);
  const end = Math.min(text.length, firstHit + q.length + 180);
  const windowed = (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');

  // Now walk the windowed string and emit <mark> spans for every hit.
  const out: React.ReactNode[] = [];
  let cursor = 0;
  const winLower = windowed.toLowerCase();
  while (cursor < windowed.length) {
    const next = winLower.indexOf(q, cursor);
    if (next < 0) {
      out.push(windowed.slice(cursor));
      break;
    }
    if (next > cursor) out.push(windowed.slice(cursor, next));
    out.push(
      <mark
        key={`m-${next}`}
        className="rounded bg-accent/30 px-0.5 text-ink"
      >
        {windowed.slice(next, next + q.length)}
      </mark>,
    );
    cursor = next + q.length;
  }
  return out;
}
